import { JxtBookingStatusEnum, ResourceTypeDtoMetaTypeEnum } from '@jooxter/api';
import { useResourceTypeRepository } from '@jooxter/resource-types';
import { IRange } from '@jooxter/utils';
import { useSearchBookings } from '../queries/queryFns';
import { useMemo } from 'react';

export const useCheckBookingOverlap = (
  organizerId: number | undefined,
  metaType: ResourceTypeDtoMetaTypeEnum.Desk | ResourceTypeDtoMetaTypeEnum.Parking | undefined,
  range: IRange | undefined,
  enabled: boolean,
  // Excludes a specific booking id from the results - needed when checking whether *moving* an
  // existing booking (e.g. drag-and-drop) creates a conflict, since that booking's own pre-move
  // record would otherwise often satisfy its own post-move range and falsely count as a conflict.
  excludeBookingId?: number
) => {
  const { repository } = useResourceTypeRepository();

  const resourceTypeIds = metaType !== undefined ? repository.findByMetaTypes([metaType]).map((rt) => rt.id) : [];

  const { bookings, isLoading } = useSearchBookings(
    {
      from: range?.from,
      to: range?.to,
      organizerId,
      resourceTypeId: resourceTypeIds,
      status: [JxtBookingStatusEnum.APPROVED, JxtBookingStatusEnum.REQUESTED],
    },
    enabled && !!organizerId && !!metaType && !!range && resourceTypeIds.length > 0
  );

  // Preserves `undefined` (still loading/not yet fetched) rather than collapsing it to `[]`, so
  // callers can distinguish "no result yet" from "confirmed no conflicts" - see useDragOverlapGuard
  // (apps/web/src/pages/Spaces/Scheduler/hooks.ts), which relies on this to avoid proceeding with a
  // drag/resize before the overlap check has actually resolved.
  const overlappingBookings = useMemo(
    () => (excludeBookingId ? bookings?.filter((booking) => booking.id !== excludeBookingId) : bookings),
    [bookings, excludeBookingId]
  );

  return { overlappingBookings, isLoading };
};
